import React, { useState } from 'react';
import { showClientLink } from '../../utils/links';
import { fetchShowAccessToken } from '../../utils/showAccessToken';

/**
 * Copies a show's secure client link straight from the projects list, so
 * staff do not have to open a project first. Mirrors CopyClientLink.tsx
 * exactly, one level up.
 *
 * **It says "Copy client link", the same as its twin (16 Oct 2026).** It
 * used to say "Copy show link", so the Clients list offered two differently
 * named actions down one column with nothing explaining the difference.
 * They are not really two actions: both copy the secure link that the
 * client opens, and the row already says whether it is a series or a single
 * project. One action, one name — the destination differs, the thing you
 * are doing does not.
 */
export function CopyShowLink({
  slug,
  showId
}: {
  slug: string;
  showId: string;
}) {
  const [state, setState] = useState<'idle' | 'busy' | 'done' | 'failed'>('idle');
  async function copy() {
    setState('busy');
    const token = await fetchShowAccessToken(slug, showId);
    const link = showClientLink(slug, token);
    if (!link) {
      setState('failed');
      window.setTimeout(() => setState('idle'), 2200);
      return;
    }
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      setState('failed');
      window.setTimeout(() => setState('idle'), 2200);
      return;
    }
    setState('done');
    window.setTimeout(() => setState('idle'), 1800);
  }
  return <button type="button" onClick={copy} className="label-sm whitespace-nowrap text-paper/30 underline underline-offset-4 transition-colors duration-150 ease-out hover:text-paper/70" data-id="element-834" data-mp="e43gbqvbux4t">
      {state === 'done' ? 'Link copied ✓' : state === 'failed' ? 'Link unavailable' : state === 'busy' ? 'Copying…' : 'Copy client link'}
    </button>;
}